import { computed, inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';

import { ProgrammingCapabilitiesRepository } from '@data/repositories';
import { TFilterProgramming } from '@data/types/params';
import {
  ProgrammingHierarchical,
  ProgrammingPercentage,
} from '@domain/builders';
import { List } from '@domain/helpers';
import {
  LongDateTableData,
  LongDateTableSummary,
} from '@core/modules/capacities/blocs';
import {
  TUpdateProgrammingCapability,
  TUpdateProgrammingParams,
} from '@domain/types';
import { ProgrammingCapability } from '../../constants';

type TProgrammingState = {
  filters: TFilterProgramming | null;
  isFetching: ProgrammingCapability[];
  capabilitiesHierarchical: ProgrammingHierarchical[];
  capabilitiesPercentages: ProgrammingPercentage[];
  listUpdateItems: TUpdateProgrammingParams[];
};

const initialState: TProgrammingState = {
  filters: null,
  isFetching: [],
  capabilitiesHierarchical: [],
  capabilitiesPercentages: [],
  listUpdateItems: [],
};

@Injectable({
  providedIn: 'root',
})
export class ProgrammingProvider {
  readonly #programmingRepository = inject(ProgrammingCapabilitiesRepository);

  readonly #state = signalState(initialState);

  readonly hierarchical = this.#state.capabilitiesHierarchical;
  readonly percentages = this.#state.capabilitiesPercentages;
  readonly listUpdateItems = this.#state.listUpdateItems;

  readonly isFetchingCapabilities = computed(
    () =>
      !!this.#state
        .isFetching()
        ?.some((key) =>
          [
            ProgrammingCapability.IS_FETCHING_HIERARCHICAL,
            ProgrammingCapability.IS_FETCHING_PERCENTAGE,
          ].includes(key),
        ),
  );

  readonly isFetchingValidation = computed(() =>
    this.#state
      .isFetching()
      .includes(ProgrammingCapability.IS_FETCHING_VALIDATE),
  );

  readonly isFetchingUpdate = computed(() =>
    this.#state.isFetching().includes(ProgrammingCapability.IS_FETCHING_UPDATE),
  );

  readonly longDateTableData = computed(
    () => new LongDateTableData(this.hierarchical()),
  );

  readonly longDateTableSummary = computed(
    () => new LongDateTableSummary(this.percentages()),
  );

  setLoading(key: ProgrammingCapability) {
    patchState(this.#state, ({ isFetching }) => ({
      isFetching: List(isFetching).add(key),
    }));
  }

  setFilters(filters: TFilterProgramming | null) {
    patchState(this.#state, { filters });
  }

  removeLoading(key: ProgrammingCapability) {
    patchState(this.#state, ({ isFetching }) => ({
      isFetching: List(isFetching).remove(key),
    }));
  }

  async getProgrammingHierarchical(params: TFilterProgramming) {
    const key = ProgrammingCapability.IS_FETCHING_HIERARCHICAL;
    this.setLoading(key);
    patchState(this.#state, {
      filters: params,
    });

    const result =
      await this.#programmingRepository.getProgrammingHierarchical(params);

    this.removeLoading(key);

    if (result.isOk === false) {
      throw result.error;
    }

    patchState(this.#state, {
      capabilitiesHierarchical: result.data,
    });
  }

  async getProgrammingPercentages(params: TFilterProgramming) {
    const key = ProgrammingCapability.IS_FETCHING_PERCENTAGE;
    this.setLoading(key);

    const result =
      await this.#programmingRepository.getProgrammingPercentages(params);

    this.removeLoading(key);

    if (result.isOk === false) {
      throw result.error;
    }

    patchState(this.#state, {
      capabilitiesPercentages: result.data,
    });
  }

  async getProgrammingData(params: TFilterProgramming) {
    return Promise.allSettled([
      this.getProgrammingHierarchical(params),
      this.getProgrammingPercentages(params),
    ]);
  }

  async validateCapability(payload: TUpdateProgrammingParams) {
    const key = ProgrammingCapability.IS_FETCHING_VALIDATE;
    this.setLoading(key);

    const formData: TUpdateProgrammingCapability = {
      ...payload,
      filialId: this.#state.filters()?.filial,
      operationId: this.#state.filters()?.operation,
    };

    const result =
      await this.#programmingRepository.validateCapabilityAvailable(formData);

    this.removeLoading(key);

    if (result.isOk === false) {
      throw result.error;
    }

    return result.data;
  }

  async updateCapabilities(payload: TUpdateProgrammingParams[]) {
    const key = ProgrammingCapability.IS_FETCHING_UPDATE;
    this.setLoading(key);

    const formData: TUpdateProgrammingCapability[] = payload.map((item) => ({
      ...item,
      filialId: this.#state.filters()?.filial,
      operationId: this.#state.filters()?.operation,
    }));

    const result =
      await this.#programmingRepository.updateMultipleCapability(formData);

    this.removeLoading(key);

    return result;
  }

  async handleUpdateCapabilities() {
    const listItems = this.#state.listUpdateItems();
    const filters = this.#state.filters();

    const result = await this.updateCapabilities(listItems);

    if (result.isOk === false) {
      throw result.error;
    }

    if (result.data.success === false) {
      return result.data;
    }

    this.clearUpdateItem();

    const resultData = await this.getProgrammingData(filters);

    const error = resultData?.find((item) => item.status === 'rejected');

    if (error) {
      throw new Error(error.reason);
    }

    return result.data;
  }

  addUpdateItem(item: TUpdateProgrammingParams) {
    patchState(this.#state, ({ listUpdateItems }) => {
      const newListItems = listUpdateItems.filter((lui) =>
        [
          lui.bagId !== item.bagId,
          lui.capabilityDate !== item.capabilityDate,
          lui.segmentedZoneId !== item.segmentedZoneId,
          lui.timeFrameId !== item.timeFrameId,
        ].some(Boolean),
      );

      if (newListItems.length === listUpdateItems.length) {
        return {
          listUpdateItems: [...listUpdateItems, item],
        };
      }

      return {
        listUpdateItems: [...newListItems, item],
      };
    });
  }

  clearUpdateItem() {
    patchState(this.#state, {
      listUpdateItems: [],
    });
  }

  async clear() {
    patchState(this.#state, initialState);
  }
}
